import enUS from '../../assets/i18n/en-US.json';
import enGB from '../../assets/i18n/en-US.json';
import nlNL from '../../assets/i18n/nl-NL.json';
import { Injectable } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { ParkbaseStorageService } from '../@shared/services/parkbaseStorage.service';
import { Subscription } from 'rxjs';

/**
 * I18nService, sets language used for translation.
 * Gets browser default language or one found in parkbaseStorage.
 */
@Injectable({
  providedIn: 'root',
})
export class I18nService {
  defaultLanguage!: string;
  supportedLanguages!: string[];

  private langChangeSubscription!: Subscription;

  constructor(
    private translateService: TranslateService,
    private parkbaseStorageService: ParkbaseStorageService
  ) {
    /**
     * Embed languages to avoid extra HTTP requests
     */
    translateService.setTranslation('en-US', enUS);
    translateService.setTranslation('en-GB', enGB);
    translateService.setTranslation('nl-NL', nlNL);
  }

  /**
   * Gets the current language.
   * @return The current language code.
   */
  get language(): string {
    const storedLang = this.parkbaseStorageService.getLanguage();
    if (storedLang) {
      return storedLang;
    } else {
      return this.translateService.currentLang;
    }
  }

  /**
   * Sets the current language.
   * Note: The current language is saved to the parkbase storage.
   * If no parameter is specified, the language is loaded from local storage (if present).
   * @param language The IETF language code to set.
   */
  set language(language: string) {
    const browserCultureLang = this.translateService.getBrowserCultureLang();
    const storedLang = this.parkbaseStorageService.getLanguage();

    if (language) {
      language = language || browserCultureLang || storedLang;
    } else {
      if (storedLang) {
        language = storedLang;
      } else {
        language = browserCultureLang ? browserCultureLang : '';
      }
    }

    let isSupportedLanguage = this.supportedLanguages.includes(language);

    /**
     * If no exact match is found, search without the region
     */
    if (language && !isSupportedLanguage) {
      language = language.split('-')[0];
      language =
        this.supportedLanguages.find((supportedLanguage) =>
          supportedLanguage.startsWith(language)
        ) || '';
      isSupportedLanguage = Boolean(language);
    }

    /**
     * Fallback if language is not supported
     */
    if (!isSupportedLanguage) {
      const browserCultureLang = this.translateService.getBrowserCultureLang();
      language = this.defaultLanguage;
      if (!language) {
        language = browserCultureLang ? browserCultureLang : '';
      }
    }

    this.translateService.use(language);
    this.parkbaseStorageService.setLanguage(language);
  }

  /**
   * Initializes i18n for the application.
   * Loads language from local storage if present, or sets default language.
   * @param defaultLanguage The default language to use.
   * @param supportedLanguages The list of supported languages.
   */
  init(defaultLanguage: string, supportedLanguages: string[]): void {
    this.defaultLanguage = defaultLanguage;
    this.supportedLanguages = supportedLanguages;
    this.language = '';

    /**
     * Warning: this subscription will always be alive for the app's lifetime
     */
    this.langChangeSubscription = this.translateService.onLangChange.subscribe(
      (event: LangChangeEvent) => {
        this.parkbaseStorageService.setLanguage(event.lang);
      }
    );
  }

  /**
   * Cleans up language change subscription.
   */
  destroy(): void {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }
}
